import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { majorToMinor, minorToMajor } from "@/core/utils/func/money-minor";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateFeeRule } from "@/features/fee-engine/api/use-create-fee-rule";
import type { FeeRuleDto } from "@/features/fee-engine/api/dto";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";
import {
  createFeeRuleSchema,
  type FeeRuleFormValues,
} from "@/features/fee-engine/schemas/fee-rule.schema";
import {
  CUSTOMER_SEGMENTS,
  FEE_CALCULATION_TYPES,
  FEE_PAYERS_FEE_ENGINE,
  FEE_PURPOSES,
  FEE_RULE_STATUSES,
  TIER_BASIS,
} from "@/features/shared/api/enums";
import { useProviderSelectOptions } from "@/features/shared/api";

const EMPTY_VALUES: FeeRuleFormValues = {
  productCode: "",
  providerId: "",
  customerSegment: "INDIVIDUAL",
  feePurpose: "CUSTOMER_FEE",
  calculationType: "PERCENTAGE",
  tierBasis: "TRANSACTION_AMOUNT",
  percentageValue: 0,
  fixedValue: 0,
  minFee: 0,
  maxFee: 0,
  currency: "GNF",
  payer: "SENDER",
  status: "DRAFT",
  effectiveFrom: "",
  effectiveTo: "",
};

const toPayload = (values: FeeRuleFormValues): FeeRuleDto => ({
  productCode: values.productCode.trim(),
  providerId: values.providerId?.trim() || null,
  customerSegment: values.customerSegment,
  feePurpose: values.feePurpose,
  calculationType: values.calculationType,
  tierBasis: values.tierBasis,
  percentageValue:
    values.calculationType === "PERCENTAGE" || values.calculationType === "TIERED"
      ? (values.percentageValue ?? null)
      : null,
  fixedValueMinor:
    values.calculationType === "FIXED"
      ? majorToMinor(values.fixedValue ?? 0, values.currency)
      : null,
  minFeeMinor:
    values.minFee != null && values.minFee > 0
      ? majorToMinor(values.minFee, values.currency)
      : null,
  maxFeeMinor:
    values.maxFee != null && values.maxFee > 0
      ? majorToMinor(values.maxFee, values.currency)
      : null,
  currency: values.currency,
  payer: values.payer,
  status: values.status,
  effectiveFrom: values.effectiveFrom,
  effectiveTo: values.effectiveTo?.trim() || null,
  tiers: [],
});

/**
 * Formulaire de création d'une règle de frais (`POST /FeeRules/add`,
 * guide §6.5). Preview obligatoire avec section frais (règle projet :
 * l'activation d'une règle a un impact tarifaire — même en DRAFT on
 * affiche la simulation).
 */
export function FeeRuleFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useFeeEngineTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createFeeRuleSchema(), [i18n.language]);

  const create = useCreateFeeRule();
  const [preview, setPreview] = useState<FeeRuleDto | null>(null);
  const { options: providerOptions, isLoading: providersLoading } =
    useProviderSelectOptions(open);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    create.mutate(preview, {
      onSuccess: () => {
        notifSilence(t("fees.rules.feedback.created"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(error.message),
    });
  };

  const fieldError =
    (name: keyof FeeRuleFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("fees.rules.create")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="productCode"
              validators={{ onChange: fieldError("productCode") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="productCode">
                    {t("fees.rules.form.productCode")}
                  </Label>
                  <Input
                    id="productCode"
                    className="notranslate"
                    placeholder={t("fees.rules.form.productCodePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="providerId">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="providerId">
                    {t("fees.rules.form.providerId")}
                  </Label>
                  <EntitySearchableSelect
                    id="providerId"
                    options={providerOptions}
                    value={field.state.value ?? ""}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={t("fees.rules.form.providerIdPlaceholder")}
                    isLoading={providersLoading}
                    clearable
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="customerSegment">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fees.rules.form.customerSegment")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as FeeRuleFormValues["customerSegment"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CUSTOMER_SEGMENTS.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="feePurpose">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fees.rules.form.feePurpose")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as FeeRuleFormValues["feePurpose"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FEE_PURPOSES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="calculationType">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fees.rules.form.calculationType")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as FeeRuleFormValues["calculationType"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FEE_CALCULATION_TYPES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="tierBasis">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fees.rules.form.tierBasis")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as FeeRuleFormValues["tierBasis"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIER_BASIS.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="percentageValue">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="percentageValue">
                    {t("fees.rules.form.percentageValue")}
                  </Label>
                  <Input
                    id="percentageValue"
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    className="notranslate"
                    placeholder={t("fees.rules.form.percentageValuePlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="fixedValue">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="fixedValue">
                    {t("fees.rules.form.fixedValue")}
                  </Label>
                  <Input
                    id="fixedValue"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={t("fees.rules.form.fixedValuePlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="minFee">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="minFee">{t("fees.rules.form.minFee")}</Label>
                  <Input
                    id="minFee"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={t("fees.rules.form.minFeePlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="maxFee">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="maxFee">{t("fees.rules.form.maxFee")}</Label>
                  <Input
                    id="maxFee"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={t("fees.rules.form.maxFeePlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="currency"
              validators={{ onChange: fieldError("currency") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="currency">
                    {t("fees.rules.form.currency")}
                  </Label>
                  <Input
                    id="currency"
                    className="uppercase notranslate"
                    maxLength={3}
                    placeholder={t("fees.rules.form.currencyPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(e.target.value.toUpperCase())
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="payer">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fees.rules.form.payer")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as FeeRuleFormValues["payer"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FEE_PAYERS_FEE_ENGINE.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="status">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fees.rules.form.status")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as FeeRuleFormValues["status"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FEE_RULE_STATUSES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field
              name="effectiveFrom"
              validators={{ onChange: fieldError("effectiveFrom") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="effectiveFrom">
                    {t("fees.rules.form.effectiveFrom")}
                  </Label>
                  <Input
                    id="effectiveFrom"
                    type="date"
                    className="notranslate"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="effectiveTo">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="effectiveTo">
                    {t("fees.rules.form.effectiveTo")}
                  </Label>
                  <Input
                    id="effectiveTo"
                    type="date"
                    className="notranslate"
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={t("fees.rules.preview.createTitle")}
          description={t("fees.rules.preview.createDescription")}
          mode="user"
          // Section frais indicative (fixe et bornes min/max si présents).
          fees={[
            ...(preview.percentageValue != null && preview.percentageValue > 0
              ? [
                  {
                    label: `${preview.percentageValue}%`,
                    amount: 0,
                    currency: preview.currency,
                  },
                ]
              : []),
            ...(preview.fixedValueMinor != null && preview.fixedValueMinor > 0
              ? [
                  {
                    label: t("fees.rules.form.fixedValue"),
                    amount: minorToMajor(preview.fixedValueMinor, preview.currency),
                    currency: preview.currency,
                  },
                ]
              : []),
          ]}
          total={{
            label: t("fees.rules.preview.totalLabel"),
            amount: minorToMajor(
              preview.fixedValueMinor ??
                preview.maxFeeMinor ??
                preview.minFeeMinor ??
                0,
              preview.currency
            ),
            currency: preview.currency,
          }}
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !create.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: create.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
